import { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import { feature, mesh } from "topojson-client";
import usStates from "us-atlas/states-10m.json";
import API_BASE from "../api";
import { COLOR_LUT, MapLegend, SCALE_MAX, formatAnomaly } from "./mapScale";

// ── Geography ─────────────────────────────────────────────────────────────────
// Continental U.S. only: drop Alaska, Hawaii, and the territories.

const NON_CONUS = new Set(["02", "15", "60", "66", "69", "72", "78"]);
const STATES = {
  type: "FeatureCollection",
  features: feature(usStates, usStates.objects.states).features.filter((f) => !NON_CONUS.has(f.id)),
};
const inConus = (geometry) => !NON_CONUS.has(geometry.id);
const STATE_BORDERS = mesh(usStates, usStates.objects.states, (a, b) => a !== b && inConus(a) && inConus(b));
const NATION_OUTLINE = mesh(usStates, usStates.objects.states, (a, b) => a === b && inConus(a));

const DEFAULT_GRID_RES = 0.5; // station map; the nClimGrid backend sends grid_res (0.25)

// ── Field sampling ────────────────────────────────────────────────────────────
// The grid arrives as cell centers on a regular lat/lon spacing. Each pixel is
// colored by blending the four surrounding cells (bilinear), skipping empty
// ones; near the coast, where thin slivers have no cell, the nearest cell
// within two cells fills in so the color reaches the shoreline.

// nClimGrid backend: { lat0, lon0, res, rows, cols, values } with nulls for no data.
function fieldFromCompact(compact) {
  return { ...compact, values: Float32Array.from(compact.values, (v) => (v == null ? NaN : v)) };
}

// Station backend: a list of { lat, lon, anomaly } cell centers.
function fieldFromList(grid, res) {
  const lat0 = d3.min(grid, (d) => d.lat);
  const lon0 = d3.min(grid, (d) => d.lon);
  const rows = Math.round((d3.max(grid, (d) => d.lat) - lat0) / res) + 1;
  const cols = Math.round((d3.max(grid, (d) => d.lon) - lon0) / res) + 1;
  const values = new Float32Array(rows * cols).fill(NaN);
  for (const { lat, lon, anomaly } of grid) {
    values[Math.round((lat - lat0) / res) * cols + Math.round((lon - lon0) / res)] = anomaly;
  }
  return { lat0, lon0, res, rows, cols, values };
}

function sampleField(field, lon, lat) {
  const { lat0, lon0, res, rows, cols, values } = field;
  const y = (lat - lat0) / res;
  const x = (lon - lon0) / res;
  const i = Math.floor(y);
  const j = Math.floor(x);
  const fy = y - i;
  const fx = x - j;
  let sum = 0;
  let weight = 0;
  for (let di = 0; di <= 1; di++) {
    for (let dj = 0; dj <= 1; dj++) {
      const r = i + di;
      const c = j + dj;
      if (r < 0 || c < 0 || r >= rows || c >= cols) continue;
      const v = values[r * cols + c];
      if (Number.isNaN(v)) continue;
      const w = (di ? fy : 1 - fy) * (dj ? fx : 1 - fx);
      sum += v * w;
      weight += w;
    }
  }
  if (weight > 0) return sum / weight;
  // Coastal fallback: nearest filled cell within two cells.
  let best = NaN;
  let bestDist = Infinity;
  const ri = Math.round(y);
  const cj = Math.round(x);
  for (let r = ri - 2; r <= ri + 2; r++) {
    for (let c = cj - 2; c <= cj + 2; c++) {
      if (r < 0 || c < 0 || r >= rows || c >= cols) continue;
      const v = values[r * cols + c];
      const d = (r - y) ** 2 + (c - x) ** 2;
      if (!Number.isNaN(v) && d < bestDist) {
        best = v;
        bestDist = d;
      }
    }
  }
  return best;
}

// National average, weighting each cell by its area (cos latitude).
function nationalAverage(field) {
  let sum = 0;
  let weight = 0;
  for (let r = 0; r < field.rows; r++) {
    const w = Math.cos(((field.lat0 + r * field.res) * Math.PI) / 180);
    for (let c = 0; c < field.cols; c++) {
      const v = field.values[r * field.cols + c];
      if (!Number.isNaN(v)) {
        sum += v * w;
        weight += w;
      }
    }
  }
  return weight ? sum / weight : null;
}

// Both backends into { defaultLayer, layers: { name: {...} } }.
function normalizeSnapshot(data) {
  if (data.layers) {
    const layers = {};
    for (const [name, layer] of Object.entries(data.layers)) {
      layers[name] = {
        asOfDate: layer.as_of_date,
        windowStart: layer.window_start,
        windowDays: layer.window_days,
        issuedAt: layer.issued_at,
        field: fieldFromCompact(layer.field),
        cities: layer.cities ?? [],
        cityCounts: layer.city_counts ?? null,
      };
    }
    return { defaultLayer: data.default_layer in layers ? data.default_layer : Object.keys(layers)[0], layers };
  }
  return {
    defaultLayer: "stations",
    layers: {
      stations: {
        asOfDate: data.as_of_date,
        windowDays: null,
        field: fieldFromList(data.grid ?? [], data.grid_res ?? DEFAULT_GRID_RES),
        cities: data.cities ?? [],
      },
    },
  };
}

// Header, headline, and source wording for each layer.
const LAYER_TEXT = {
  today: {
    tab: "Today",
    eyebrow: "U.S. TODAY · FORECAST",
    across: "Across the U.S. today",
    period: "today",
    sub: (l) => `Today's forecast high (${formatDate(l.asOfDate)}) compared with the 1951–1980 normal for this date.`,
    source: (l) =>
      `Source: NWS National Digital Forecast Database${l.issuedAt ? `, issued ${formatIssued(l.issuedAt)}` : ""}, averaged into 0.25° cells. ` +
      "Normal: NOAA nClimGrid-Daily, 1951–1980 average for this calendar day.",
  },
  past_week: {
    tab: "Past week",
    eyebrow: "U.S. THIS PAST WEEK",
    across: "Across the U.S. this past week",
    period: "this past week",
    sub: (l) => `Average daily high over the ${l.windowDays} days ending ${formatDate(l.asOfDate)}, compared with the same days in 1951–1980.`,
    source: () =>
      "Source: NOAA nClimGrid-Daily (recent days preliminary). nClimGrid runs about three days behind, so the latest days " +
      "use each day's NWS forecast high until observations arrive. Averaged into 0.25° cells. Normal: the 1951–1980 average for each calendar day.",
  },
  stations: {
    eyebrow: "U.S. TODAY",
    across: "Across the U.S.",
    period: "today",
    sub: () => "Daily high temperature vs. the 1951–1980 baseline, across the continental U.S.",
    source: () => "Based on NOAA GHCN daily station records. IDW interpolation between stations — quality varies in data-sparse regions.",
  },
};

function formatDate(isoDate) {
  return isoDate
    ? new Date(`${isoDate}T12:00:00`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "…";
}

function formatIssued(isoTimestamp) {
  return new Date(isoTimestamp).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" });
}

// ── Map ───────────────────────────────────────────────────────────────────────

function useWidth(ref) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

// field is null while the data loads: the states (bundled with the app) are
// drawn right away as a pulsing silhouette, and the colors fade in on arrival.
// message, if given, is shown over the silhouette (e.g. data unavailable).
function UsAnomalyField({ field, message }) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const width = useWidth(containerRef);
  const [hover, setHover] = useState(null);
  const [painted, setPainted] = useState(false);


  const { projection, height } = useMemo(() => {
    if (!width) return { projection: null, height: 0 };
    const proj = d3.geoAlbers().fitWidth(width, STATES);
    const [, [, y1]] = d3.geoPath(proj).bounds(STATES);
    return { projection: proj, height: Math.ceil(y1) + 1 };
  }, [width]);

  const path = useMemo(() => (projection ? d3.geoPath(projection) : null), [projection]);

  // Paint the field once per size: invert each pixel to lat/lon, sample, color.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !projection || !field) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);

    // Sample at CSS-pixel resolution; the field is smooth, so upscaling is invisible.
    const offscreen = document.createElement("canvas");
    offscreen.width = width;
    offscreen.height = height;
    const offCtx = offscreen.getContext("2d");
    const image = offCtx.createImageData(width, height);
    const data = image.data;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const lonLat = projection.invert([x + 0.5, y + 0.5]);
        if (!lonLat) continue;
        const v = sampleField(field, lonLat[0], lonLat[1]);
        if (Number.isNaN(v)) continue;
        const idx = Math.max(0, Math.min(255, Math.round(((v + SCALE_MAX) / (2 * SCALE_MAX)) * 255)));
        const [r, g, b] = COLOR_LUT[idx];
        const o = (y * width + x) * 4;
        data[o] = r;
        data[o + 1] = g;
        data[o + 2] = b;
        data[o + 3] = 255;
      }
    }
    offCtx.putImageData(image, 0, 0);

    // Clip to the exact national outline so the coast is crisp.
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    ctx.save();
    ctx.beginPath();
    d3.geoPath(projection, ctx)(STATES);
    ctx.clip();
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(offscreen, 0, 0, width, height);
    ctx.restore();
    setPainted(true);
  }, [field, projection, width, height]);

  const handleMove = (event) => {
    if (!projection || !field) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const lonLat = projection.invert([x, y]);
    const state = lonLat && STATES.features.find((f) => d3.geoContains(f, lonLat));
    const value = state ? sampleField(field, lonLat[0], lonLat[1]) : NaN;
    setHover(state && !Number.isNaN(value) ? { x, y, state: state.properties.name, value } : null);
  };

  return (
    <div ref={containerRef} className="us-map" style={{ height: height || undefined }} aria-busy={!painted}>
      {projection && (
        <>
          <svg className="us-map-underlay" width={width} height={height} aria-hidden="true">
            <path d={path(STATES)} className={`us-map-silhouette ${field || message ? "" : "us-map-silhouette--pulse"}`} />
          </svg>
          <canvas
            ref={canvasRef}
            className={`us-map-field ${painted ? "us-map-field--painted" : ""}`}
            style={{ width, height }}
          />
          <svg
            className="us-map-overlay"
            width={width}
            height={height}
            onMouseMove={handleMove}
            onMouseLeave={() => setHover(null)}
            role="img"
            aria-label="Map of the continental United States colored by how much warmer or cooler daily highs were than the 1951–1980 normal"
          >
            <path d={path(STATE_BORDERS)} className="us-map-states" />
            <path d={path(NATION_OUTLINE)} className="us-map-nation" />
            {hover && (
              <path
                d={path(STATES.features.find((f) => f.properties.name === hover.state))}
                className="us-map-hover-state"
              />
            )}
          </svg>
          {message && <div className="us-map-message">{message}</div>}
          {hover && (
            <div
              className="us-map-tooltip"
              style={{ left: Math.min(hover.x + 14, width - 170), top: Math.max(hover.y - 46, 0) }}
            >
              <span className="us-map-tooltip-state">{hover.state}</span>
              <span className={hover.value >= 0 ? "legend-hot" : "legend-cool"}>
                {formatAnomaly(hover.value)} vs. 1951–80
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── City card ─────────────────────────────────────────────────────────────────

function CityCard({ city, onSelect }) {
  const isHot = city.anomaly > 0;
  return (
    <button className="map-city-card" onClick={() => onSelect(city)} title={`View data for ${city.city}, ${city.state}`}>
      <span className="map-city-name">
        {city.city}, {city.state}
      </span>
      <span className={`map-city-anomaly ${isHot ? "map-city-anomaly--hot" : "map-city-anomaly--cold"}`}>
        {formatAnomaly(city.anomaly)}
      </span>
    </button>
  );
}

// ── Data: fetched once per session ────────────────────────────────────────────

// The request starts as soon as this module loads (with the app, before the
// landing page renders), and the result is kept for the session, so returning
// from a city page shows the map instantly. A failed request is retried on the
// next visit.
let snapshotRequest = null;
let snapshotCache = null;
let lastLayerName = null; // the visitor's toggle choice, kept across visits

function loadSnapshot() {
  if (!snapshotRequest) {
    snapshotRequest = fetch(`${API_BASE}/map-snapshot`)
      .then((r) => {
        if (!r.ok) throw new Error(r.status);
        return r.json();
      })
      .then((data) => {
        snapshotCache = normalizeSnapshot(data);
        return snapshotCache;
      });
    snapshotRequest.catch(() => {
      snapshotRequest = null;
    });
  }
  return snapshotRequest;
}

loadSnapshot();

const hasField = (snapshot) => Boolean(snapshot?.layers[snapshot.defaultLayer]?.field.values.length);
const initialLayer = (snapshot) =>
  snapshot && lastLayerName && snapshot.layers[lastLayerName] ? lastLayerName : snapshot?.defaultLayer ?? null;

// ── Main component ────────────────────────────────────────────────────────────

// The landing page below the search: a split hero (the `hero` content plus the
// share of cities warmer than normal on the left, the U.S. map on the right),
// then `middle` (e.g. warming stripes), then the hottest/coolest city cards.
// The Today / Past week toggle drives the map, the city share, and the cards.
export default function AnomalyMap({ onCitySelect, hero, middle }) {
  const [snapshot, setSnapshot] = useState(snapshotCache);
  const [layerName, setLayerNameState] = useState(() => initialLayer(snapshotCache));
  const [status, setStatus] = useState(snapshotCache ? (hasField(snapshotCache) ? "ready" : "error") : "loading");

  useEffect(() => {
    if (snapshotCache) return undefined;
    let live = true;
    loadSnapshot()
      .then((normalized) => {
        if (!live) return;
        setSnapshot(normalized);
        setLayerNameState(initialLayer(normalized));
        setStatus(hasField(normalized) ? "ready" : "error");
      })
      .catch(() => live && setStatus("error"));
    return () => {
      live = false;
    };
  }, []);

  const setLayerName = (name) => {
    lastLayerName = name;
    setLayerNameState(name);
  };

  const layer = snapshot?.layers[layerName];
  const text = LAYER_TEXT[layerName] ?? LAYER_TEXT.stations;
  // Before the data arrives both tabs are shown (disabled) so nothing shifts.
  const tabs = snapshot ? ["today", "past_week"].filter((name) => snapshot.layers[name]) : ["today", "past_week"];
  const average = useMemo(() => (layer ? nationalAverage(layer.field) : null), [layer]);
  const hotCities = (layer?.cities ?? []).filter((c) => c.rank_group === "hot").slice(0, 5);
  const coldCities = (layer?.cities ?? []).filter((c) => c.rank_group === "cold").slice(0, 5);
  const counts = layer?.cityCounts;
  const share = counts?.total ? Math.round((100 * counts.warmer) / counts.total) : null;

  return (
    <>
      <section className="landing-split">
        <div className="landing-split-text">
          {hero}
          {status === "loading" && (
            <div className="landing-stat" aria-hidden="true">
              <span className="skeleton skeleton-stat" />
              <span className="skeleton skeleton-line" />
              <span className="skeleton skeleton-line skeleton-line--short" />
            </div>
          )}
          {status === "ready" && share != null && (
            <div className="landing-stat">
              <span className={`landing-stat-number ${share >= 50 ? "legend-hot" : "legend-cool"}`}>{share}%</span>
              <span className="landing-stat-caption">
                of cities are warmer than normal {text.period}:{" "}
                <strong>
                  {counts.warmer.toLocaleString()} of {counts.total.toLocaleString()}
                </strong>{" "}
                across the continental U.S.
              </span>
            </div>
          )}
        </div>

        <div className="landing-split-map">
          {tabs.length > 1 && (
            <div className="map-toggle" role="tablist" aria-label="Map period">
              {tabs.map((name) => (
                <button
                  key={name}
                  role="tab"
                  aria-selected={name === layerName}
                  disabled={!snapshot}
                  className={`map-toggle-option ${name === (layerName ?? "today") ? "map-toggle-option--active" : ""}`}
                  onClick={() => setLayerName(name)}
                >
                  {LAYER_TEXT[name].tab}
                </button>
              ))}
            </div>
          )}
          {status === "ready" && average != null && (
            <p className="map-headline">
              {text.across}:{" "}
              <strong className={average >= 0 ? "legend-hot" : "legend-cool"}>
                {formatAnomaly(average)} {average >= 0 ? "hotter" : "cooler"}
              </strong>{" "}
              than normal
            </p>
          )}
          {status === "loading" && (
            <p className="map-headline" aria-hidden="true">
              <span className="skeleton skeleton-line skeleton-line--headline" />
            </p>
          )}
          <UsAnomalyField
            field={status === "ready" ? layer?.field ?? null : null}
            message={status === "error" ? "Map data unavailable right now." : null}
          />
          <MapLegend />
          {status === "ready" && layer ? (
            <p className="landing-map-sub">{text.sub(layer)}</p>
          ) : (
            <p className="landing-map-sub" aria-hidden="true">
              <span className="skeleton skeleton-line skeleton-line--sub" />
            </p>
          )}
        </div>
      </section>

      {middle}

      {status === "ready" && (hotCities.length > 0 || coldCities.length > 0) && (
        <section className="landing-extremes">
          <div className="landing-section-header">
            <p className="landing-eyebrow">{layerName === "past_week" ? "THIS PAST WEEK'S EXTREMES" : "TODAY'S EXTREMES"}</p>
            <h2 className="landing-section-title">Where it's most unusual right now</h2>
          </div>
          <div className="map-cities">
            {hotCities.length > 0 && (
              <div className="map-cities-group">
                <p className="map-cities-label map-cities-label--hot">Notably hotter</p>
                {hotCities.map((c) => (
                  <CityCard key={c.city_id ?? c.station_id} city={c} onSelect={onCitySelect} />
                ))}
              </div>
            )}
            {coldCities.length > 0 && (
              <div className="map-cities-group">
                <p className="map-cities-label map-cities-label--cold">Notably cooler</p>
                {coldCities.map((c) => (
                  <CityCard key={c.city_id ?? c.station_id} city={c} onSelect={onCitySelect} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {layer && <p className="map-source-note">{text.source(layer)}</p>}
    </>
  );
}
